import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { SafePipe } from '../../pipes/safe.pipe';

@Component({
  selector: 'app-request-renew-appraiser-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StaffHeaderComponent,
    StaffNavComponent,
    SafePipe
  ],
  templateUrl: './request-renew-appraiser-form.component.html',
  styleUrl: './request-renew-appraiser-form.component.scss'
})
export class RequestRenewAppraiserFormComponent implements OnInit, OnDestroy {
  requestId: string = ''; // Request ID from route params
  userId: string = ''; // User ID from request data
  
  registrationForm!: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  isSubmitting = false;
  isLoading = false;
  error: string = '';
  
  // Original data for comparison
  originalData: any = null;
  
  // Documents from API
  documents: Array<{name: string, file: string}> = [];
  
  // CPD Hours data from API
  cpdHoursData: any[] = [];
  
  // Dynamic arrays for display
  valuationExperience: any[] = [];
  educationHistory: any[] = [];
  specialCourses: any[] = [];
  valuationLogs: any[] = [];
  
  // Appraiser level from API
  appraiserLevel: string = '';

  // Payment slip URL from API
  paymentSlip: string = '';

  // Receipt code
  existingCodeReceipt: string = '';
  
  private subscription = new Subscription();

  // Dropdown options
  statusOptions = [
    { value: 'รอตรวจเอกสาร', label: 'รอตรวจเอกสาร', class: 'bg-secondary' },
    { value: 'ผ่านการตรวจสอบ', label: 'ผ่านการตรวจสอบ', class: 'bg-info' },
    { value: 'ดำเนินการออกบัตร', label: 'ดำเนินการออกบัตร', class: 'bg-warning' },
    { value: 'ดำเนินการเสร็จสิ้น', label: 'ดำเนินการเสร็จสิ้น', class: 'bg-success' },
    { value: 'ยกเลิก', label: 'ยกเลิก', class: 'bg-danger' }
  ];

  paymentStatusOptions = [
    { value: '-', label: '-', class: 'bg-secondary' },
    { value: 'รอชำระเงิน', label: 'รอชำระเงิน', class: 'bg-warning' },
    { value: 'ชำระเงินเสร็จสิ้น', label: 'ชำระเงินเสร็จสิ้น', class: 'bg-success' },
    { value: 'ไม่ผ่านการตรวจสอบ', label: 'ไม่ผ่านการตรวจสอบ', class: 'bg-danger' },
    { value: 'รอตรวจสอบยอดชำระ', label: 'รอตรวจสอบยอดชำระ', class: 'bg-info' },
    { value: 'ยกเลิกการชำระ', label: 'ยกเลิกการชำระ', class: 'bg-dark' }
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Get requestId from route params
    this.route.params.subscribe(params => {
      this.requestId = params['requestId'];
      
      if (this.requestId) {
        this.loadRequestData();
      } else {
        this.error = 'ไม่พบข้อมูล Request ID';
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.registrationForm = this.fb.group({
      // Request Information (Read-Only in Step 1)
      status: ['รอตรวจเอกสาร', Validators.required],
      paymentStatus: ['-', Validators.required],
      cardNumber: [''],
      cardExpiryDate: [''],
      
      // User Information (Read-Only)
      titleName: [{ value: '', disabled: true }],
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      idCard: [{ value: '', disabled: true }],
      dateOfBirth: [{ value: '', disabled: true }],
      nationality: [{ value: '', disabled: true }],
      phoneNumber: [{ value: '', disabled: true }],
      lineId: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }],
      
      // Current Address (Read-Only)
      currentHouseNumber: [{ value: '', disabled: true }],
      currentVillage: [{ value: '', disabled: true }],
      currentSoi: [{ value: '', disabled: true }],
      currentRoad: [{ value: '', disabled: true }],
      currentSubdistrict: [{ value: '', disabled: true }],
      currentDistrict: [{ value: '', disabled: true }],
      currentProvince: [{ value: '', disabled: true }],
      currentPostalCode: [{ value: '', disabled: true }],
      
      // Registered Address (Read-Only)
      registeredHouseNumber: [{ value: '', disabled: true }],
      registeredVillage: [{ value: '', disabled: true }],
      registeredSoi: [{ value: '', disabled: true }],
      registeredRoad: [{ value: '', disabled: true }],
      registeredSubdistrict: [{ value: '', disabled: true }],
      registeredDistrict: [{ value: '', disabled: true }],
      registeredProvince: [{ value: '', disabled: true }],
      registeredPostalCode: [{ value: '', disabled: true }],
      
      // Additional fields
      requestId: [''],
      createdAt: [''],
      updatedAt: [''],
      feeAmount: [{ value: 0, disabled: true }]
    });
  }

  /**
   * Load request data from API
   */
  private loadRequestData(): void {
    this.error = '';
    
    // Use the request ID directly - CHANGED API ENDPOINT
    const apiUrl = `${environment.apiUrl}/form-renew-appraiser/${this.requestId}`;
    
    console.log('🔍 Loading request data from:', apiUrl);
    
    this.http.get(apiUrl).subscribe({
      next: (response: any) => {
        console.log('✅ Request data loaded:', response);
        this.originalData = response;
        
        // Store appraiser level from response
        if (response.appraiserLevel) {
          this.appraiserLevel = response.appraiserLevel;
          console.log('📊 Appraiser level:', this.appraiserLevel);
        }
        
        // Load documents if available
        if (response.documents && Array.isArray(response.documents)) {
          this.documents = response.documents;
          console.log('📄 Documents loaded:', this.documents);
        }

        // Load payment slip if available (หลักฐานการชำระเงินที่ผู้สมัครอัปโหลด)
        this.paymentSlip = response.paymentSlip || '';
        if (this.paymentSlip) {
          console.log('💳 Payment slip loaded:', this.paymentSlip);
        }
        
        // Load valuation logs if available
        if (response.valuationLogs && Array.isArray(response.valuationLogs)) {
          this.valuationLogs = response.valuationLogs;
          console.log('📊 Valuation logs loaded:', this.valuationLogs);
        }
        
        // Extract userId and load user information
        if (response.userId) {
          this.loadUserInformation(response.userId, response);
        } else {
          console.warn('⚠️ No userId found in request data');
          this.populateForm(response);
        }
      },
      error: (error) => {
        console.error('❌ Error loading request data:', error);
        this.error = error.error?.message || 'ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง';
      }
    });
  }

  /**
   * Load user information from user-information API
   */
  private loadUserInformation(userId: string, requestData: any): void {
    const userApiUrl = `${environment.apiUrl}/user-information/user/${userId}`;
    
    // Store userId for later use
    this.userId = userId;
    
    console.log('🔍 Loading user information from:', userApiUrl);
    
    this.http.get(userApiUrl).subscribe({
      next: (userInfo: any) => {
        console.log('✅ User information loaded:', userInfo);
        
        // Store user information in originalData for dynamic arrays access
        if (!this.originalData.user) {
          this.originalData.user = {};
        }
        this.originalData.user.userInformation = userInfo;
        
        // Populate dynamic arrays directly
        this.valuationExperience = userInfo.valuationExperience || [];
        this.educationHistory = userInfo.educationHistory || [];
        this.specialCourses = userInfo.specialCourses || [];
        
        console.log('📊 Dynamic arrays populated:', {
          valuationExperience: this.valuationExperience.length,
          educationHistory: this.educationHistory.length,
          specialCourses: this.specialCourses.length
        });
        
        // Load CPD hours data
        this.loadCpdHours(userId);
        
        // Merge request data with user information
        this.populateForm(requestData, userInfo);
      },
      error: (error) => {
        console.error('❌ Error loading user information:', error);
        console.warn('⚠️ Falling back to request data only');
        // Fallback to request data only
        this.populateForm(requestData);
      }
    });
  }

  /**
   * Load CPD hours data from API
   */
  private loadCpdHours(userId: string): void {
    const cpdApiUrl = `${environment.apiUrl}/cpd-hours/user/${userId}`;
    
    console.log('🔍 Loading CPD hours from:', cpdApiUrl);
    
    this.http.get(cpdApiUrl).subscribe({
      next: (response: any) => {
        console.log('✅ CPD hours loaded:', response);
        this.cpdHoursData = Array.isArray(response) ? response : [];
        console.log('📊 CPD hours count:', this.cpdHoursData.length);
      },
      error: (error) => {
        console.error('❌ Error loading CPD hours:', error);
        this.cpdHoursData = [];
      }
    });
  }

  /**
   * Populate form with API data
   */
  private populateForm(requestData: any, userInfo?: any): void {
    console.log('📝 Populating form with request data:', requestData);
    console.log('📝 Populating form with user info:', userInfo);
    
    try {
      // Store existing receipt code if present
      this.existingCodeReceipt = requestData.codeReceipt || '';

      // Populate request fields (Step 1 fields are disabled/read-only)
      this.registrationForm.patchValue({
        status: requestData.status || 'รอตรวจเอกสาร',
        paymentStatus: requestData.paymentStatus || '-',
        requestId: requestData.id || '',
        createdAt: requestData.createdAt || '',
        updatedAt: requestData.updatedAt || '',
        feeAmount: requestData.feeAmount || 0
      }, { emitEvent: false });

      // Populate user information from user-information API if available
      if (userInfo) {
        this.registrationForm.patchValue({
          // Personal Information
          firstName: userInfo.firstNameThai || userInfo.firstName || '',
          lastName: userInfo.lastNameThai || userInfo.lastName || '',
          idCard: userInfo.user?.cid || '',
          dateOfBirth: userInfo.dateOfBirth || '',
          nationality: userInfo.nationality || '',
          phoneNumber: userInfo.phoneNumber || userInfo.currentPhone || '',
          lineId: userInfo.currentLineId || '',
          email: userInfo.user?.email || '',
          
          // Current Address - Map from flat structure
          currentHouseNumber: userInfo.currentAddress || userInfo.address || '',
          currentSubdistrict: userInfo.currentSubdistrict || userInfo.subdistrict || '',
          currentDistrict: userInfo.currentDistrict || userInfo.district || '',
          currentProvince: userInfo.currentProvince || userInfo.province || '',
          currentPostalCode: userInfo.currentPostalCode || userInfo.postalCode || '',
          
          // Registered Address - Use same as current if not provided separately
          registeredHouseNumber: userInfo.address || userInfo.currentAddress || '',
          registeredSubdistrict: userInfo.subdistrict || userInfo.currentSubdistrict || '',
          registeredDistrict: userInfo.district || userInfo.currentDistrict || '',
          registeredProvince: userInfo.province || userInfo.currentProvince || '',
          registeredPostalCode: userInfo.postalCode || userInfo.currentPostalCode || '',
          
          // Appraiser Card Information (editable fields)
          // Use valuerId and valuerExpired, with null/empty value handling
          cardNumber: this.getValidValue(userInfo.valuerId),
          cardExpiryDate: this.getValidValue(userInfo.valuerExpired)
        }, { emitEvent: false });
        
        console.log('✅ Form populated successfully with user information from user-information API');
      } 
      // Fallback to old structure if userInfo not provided
      else if (requestData.user && requestData.user.userInformation) {
        const oldUserInfo = requestData.user.userInformation;
        
        this.registrationForm.patchValue({
          titleName: oldUserInfo.titleName || '',
          firstName: oldUserInfo.firstName || '',
          lastName: oldUserInfo.lastName || '',
          idCard: requestData.user.cid || '',
          dateOfBirth: oldUserInfo.dateOfBirth || '',
          nationality: oldUserInfo.nationality || '',
          phoneNumber: oldUserInfo.phoneNumber || '',
          lineId: oldUserInfo.lineId || '',
          email: requestData.user.email || '',
          
          // Current Address
          currentHouseNumber: oldUserInfo.currentAddress?.houseNumber || '',
          currentVillage: oldUserInfo.currentAddress?.village || '',
          currentSoi: oldUserInfo.currentAddress?.soi || '',
          currentRoad: oldUserInfo.currentAddress?.road || '',
          currentSubdistrict: oldUserInfo.currentAddress?.subdistrict || '',
          currentDistrict: oldUserInfo.currentAddress?.district || '',
          currentProvince: oldUserInfo.currentAddress?.province || '',
          currentPostalCode: oldUserInfo.currentAddress?.postalCode || '',
          
          // Registered Address
          registeredHouseNumber: oldUserInfo.registeredAddress?.houseNumber || '',
          registeredVillage: oldUserInfo.registeredAddress?.village || '',
          registeredSoi: oldUserInfo.registeredAddress?.soi || '',
          registeredRoad: oldUserInfo.registeredAddress?.road || '',
          registeredSubdistrict: oldUserInfo.registeredAddress?.subdistrict || '',
          registeredDistrict: oldUserInfo.registeredAddress?.district || '',
          registeredProvince: oldUserInfo.registeredAddress?.province || '',
          registeredPostalCode: oldUserInfo.registeredAddress?.postalCode || ''
        }, { emitEvent: false });
        
        console.log('✅ Form populated successfully with user information from request data (fallback)');
      } else {
        console.warn('⚠️ User information not found in response, only request data populated');
      }
    } catch (error) {
      console.error('❌ Error populating form:', error);
      // Don't throw error, just log it - form will show with partial data
    }
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps && step <= this.currentStep) {
      this.currentStep = step;
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    if (this.currentStep === 1) {
      // Step 1 (Personal Information) is read-only, always valid
      return true;
    }
    
    if (this.currentStep === 2) {
      // Validate status field in Step 2 (Review and Confirm)
      const statusControl = this.registrationForm.get('status');
      const isValid = statusControl ? statusControl.valid : false;
      
      if (!isValid) {
        alert('กรุณาเลือกสถานะคำขอ');
      }
      
      return isValid;
    }
    
    if (this.currentStep === 3) {
      // Validate payment status field in Step 3
      const paymentStatusControl = this.registrationForm.get('paymentStatus');
      const isValid = paymentStatusControl ? paymentStatusControl.valid : false;
      
      if (!isValid) {
        alert('กรุณาเลือกสถานะการชำระเงิน');
      }
      
      return isValid;
    }
    
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.registrationForm.get(fieldName);
    if (!control) return false;
    
    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }
    
    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.registrationForm.get(fieldName);
    if (!control || !control.errors) return '';
    
    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['min']) return `ค่าต้องไม่น้อยกว่า ${control.errors['min'].min}`;
    
    return 'ข้อมูลไม่ถูกต้อง';
  }

  /**
   * Submit form (Update request)
   */
  onSubmit(): void {
    if (this.registrationForm.valid) {
      this.isSubmitting = true;
      
      const requestId = this.registrationForm.get('requestId')?.value;
      
      if (!requestId) {
        alert('ไม่พบข้อมูลคำขอ');
        this.isSubmitting = false;
        return;
      }
      
      // Prepare update data for request (without cardNumber and cardExpiryDate)
      const updateData: any = {
        status: this.registrationForm.get('status')?.value
      };
      
      // Prepare appraiser data for user-information
      // Map ประเภทสมาชิก to valuerType, กรอกเลขที่บัตร to valuerId, วันหมดอายุบัตร to valuerExpired
      const appraiserData = {
        valuerType: this.appraiserLevel || '',
        valuerId: this.registrationForm.get('cardNumber')?.value || '',
        valuerExpired: this.registrationForm.get('cardExpiryDate')?.value || null
      };
      
      // Send PATCH request to update request - CHANGED API ENDPOINT
      const requestApiUrl = `${environment.apiUrl}/form-renew-appraiser/${requestId}`;
      
      console.log('📤 Updating request:', updateData);
      console.log('📤 Appraiser data to be sent:', appraiserData);
      
      this.http.patch(requestApiUrl, updateData).subscribe({
        next: (response: any) => {
          console.log('✅ Request updated successfully:', response);
          
          // If userId exists, update user-information with appraiser data
          if (this.userId) {
            this.updateUserInformation(appraiserData);
          } else {
            this.isSubmitting = false;
            alert('บันทึกข้อมูลสำเร็จ');
            this.router.navigate(['/staff/request-renew-appraiser']);
          }
        },
        error: (error) => {
          console.error('❌ Error updating request:', error);
          this.isSubmitting = false;
          
          const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง';
          alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
        }
      });
    } else {
      Object.keys(this.registrationForm.controls).forEach(key => {
        this.registrationForm.get(key)?.markAsTouched();
      });
      
      alert('กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง');
    }
  }

  /**
   * Update user information with appraiser data
   */
  private updateUserInformation(appraiserData: any): void {
    const userApiUrl = `${environment.apiUrl}/user-information/user/${this.userId}`;
    
    console.log('📤 Updating user information:', appraiserData);
    
    this.http.patch(userApiUrl, appraiserData).subscribe({
      next: (response: any) => {
        console.log('✅ User information updated successfully:', response);
        this.isSubmitting = false;
        
        alert('บันทึกข้อมูลสำเร็จ');
        
        // Navigate back to list - CHANGED NAVIGATION
        this.router.navigate(['/staff/request-renew-appraiser']);
      },
      error: (error) => {
        console.error('❌ Error updating user information:', error);
        this.isSubmitting = false;
        
        const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูลผู้ประเมินหลัก';
        alert(`เกิดข้อผิดพลาด: ${errorMessage}`);
      }
    });
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลส่วนตัว';
      case 2: return 'ยืนยันและบันทึก';
      case 3: return 'สถานะการชำระเงิน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-person';
      case 2: return 'bi-check-circle';
      case 3: return 'bi-credit-card';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Go back to list
   */
  goBack(): void {
    if (confirm('คุณต้องการออกจากหน้านี้หรือไม่? การเปลี่ยนแปลงที่ยังไม่ได้บันทึกจะหายไป')) {
      this.router.navigate(['/staff/request-renew-appraiser']);
    }
  }

  /**
   * Get appraiser level label for review
   */
  getAppraiserLevelLabel(): string {
    return this.appraiserLevel || '-';
  }

  /**
   * สถานะการชำระเงินของคำขอนี้ (แสดงอย่างเดียว)
   *
   * การปรับสถานะและการออกใบเสร็จเป็นหน้าที่ของฝ่ายการเงิน ทำที่เมนู
   * "ตรวจสอบการชำระเงิน" (/staff/payment-verification) ไม่ใช่ที่หน้าใบคำขอนี้
   */
  get paymentStatusDisplay(): string {
    return this.registrationForm.get('paymentStatus')?.value || '-';
  }

  /**
   * Helper method to get valid value, handling null, empty string, and "ไม่มี"
   */
  private getValidValue(value: any): string {
    // Check for null, undefined, empty string, or "ไม่มี"
    if (!value || value === '' || value === 'ไม่มี' || value === null || value === undefined) {
      return '';
    }
    return value;
  }

  /**
   * Get status label
   */
  getStatusLabel(): string {
    const value = this.registrationForm.get('status')?.value;
    const status = this.statusOptions.find(s => s.value === value);
    return status?.label || '-';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(): string {
    const value = this.registrationForm.get('status')?.value;
    const status = this.statusOptions.find(s => s.value === value);
    return status?.class || 'bg-secondary';
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const parts = [
      this.registrationForm.get(`${prefix}HouseNumber`)?.value,
      this.registrationForm.get(`${prefix}Village`)?.value,
      this.registrationForm.get(`${prefix}Soi`)?.value,
      this.registrationForm.get(`${prefix}Road`)?.value,
      this.registrationForm.get(`${prefix}Subdistrict`)?.value,
      this.registrationForm.get(`${prefix}District`)?.value,
      this.registrationForm.get(`${prefix}Province`)?.value,
      this.registrationForm.get(`${prefix}PostalCode`)?.value
    ].filter(part => part && part.trim() !== '');

    return parts.join(' ') || '-';
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Get payment status label
   */
  getPaymentStatusLabel(): string {
    const value = this.registrationForm.get('paymentStatus')?.value;
    const status = this.paymentStatusOptions.find(s => s.value === value);
    return status?.label || '-';
  }

  /**
   * Get payment status badge class
   */
  getPaymentStatusBadgeClass(): string {
    const value = this.registrationForm.get('paymentStatus')?.value;
    const status = this.paymentStatusOptions.find(s => s.value === value);
    return status?.class || 'bg-secondary';
  }

  /**
   * Get valuation experience array (read-only)
   */
  get valuationExperienceList(): any[] {
    return this.valuationExperience;
  }

  /**
   * Get education background array (read-only)
   */
  get educationBackgroundList(): any[] {
    return this.educationHistory;
  }

  /**
   * Get special courses array (read-only)
   */
  get specialCoursesList(): any[] {
    return this.specialCourses;
  }

  /**
   * Get VAT courses array (read-only) - Now from CPD Hours API
   */
  get vatCoursesList(): any[] {
    return this.cpdHoursData || [];
  }

  /**
   * Get valuation logs array (read-only)
   */
  get valuationLogsList(): any[] {
    return this.valuationLogs || [];
  }

  /**
   * Calculate total CPD hours from verified courses only
   */
  calculateTotalCpdHours(): number {
    if (!this.cpdHoursData || this.cpdHoursData.length === 0) {
      return 0;
    }
    
    return this.cpdHoursData.reduce((total, course) => {
      // Only count hours if isVerified is true
      if (course.isVerified === true) {
        const hours = parseFloat(course.course?.duration || course.cpdHours || 0);
        return total + hours;
      }
      return total;
    }, 0);
  }

  /**
   * Check if file is an image
   */
  isImageFile(fileName: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  }

  /**
   * Check if file is a PDF
   */
  isPdfFile(fileName: string): boolean {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  /**
   * Get file icon class based on file type
   */
  getFileIcon(fileName: string): string {
    if (this.isImageFile(fileName)) {
      return 'bi-file-earmark-image';
    } else if (this.isPdfFile(fileName)) {
      return 'bi-file-earmark-pdf';
    }
    return 'bi-file-earmark';
  }

  /**
   * Open document in new tab
   */
  openDocument(fileUrl: string): void {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }

  /**
   * มีหลักฐานการชำระเงินที่ผู้สมัครอัปโหลดไว้หรือไม่
   */
  hasPaymentSlip(): boolean {
    return !!this.paymentSlip && this.paymentSlip.trim() !== '';
  }

  /**
   * เปิดดูหลักฐานการชำระเงินขนาดเต็ม
   */
  openPaymentSlipPreview(): void {
    if (this.hasPaymentSlip()) {
      window.open(this.paymentSlip, '_blank');
    }
  }
}
